import { Injectable } from '@angular/core';
import { Capacitor } from '@capacitor/core';

@Injectable({
  providedIn: 'root',
})
export class SystemUIService {
  private isAndroid = Capacitor.getPlatform() === 'android';
  private isIOS = Capacitor.getPlatform() === 'ios';
  private isWebMobile = Capacitor.getPlatform() === 'web' && /Mobi|Android/i.test(navigator.userAgent);

  constructor() {
    this.initSystemUIDetection();
  }

  private async initSystemUIDetection() {
    // Set initial values
    await this.updateSystemUIPadding();
    this.detectAndApplyFontScale();

    // Listen for viewport changes (orientation, keyboard, etc.)
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', () => {
        this.updateSystemUIPadding();
      });
    }

    // Fallback for browsers without visualViewport
    window.addEventListener('resize', () => {
      this.updateSystemUIPadding();
    });

    // Listen for orientation changes
    window.addEventListener('orientationchange', () => {
      setTimeout(() => {
        this.updateSystemUIPadding();
      }, 200); // Small delay to ensure orientation change is complete
    });
  }

  private async updateSystemUIPadding() {
    let statusBarHeight = 0;
    let navigationBarHeight = 0;

    // Use calculation-based approach for all platforms
    statusBarHeight = this.calculateStatusBarHeight();
    navigationBarHeight = this.calculateNavigationBarHeight();

    // Set CSS custom properties with small buffer - keep it minimal
    const headerPadding = Math.max(statusBarHeight + 5, 15); // Minimum 15px, small buffer
    const footerPadding = Math.max(navigationBarHeight + 5, 25); // Minimum 25px, small buffer

    document.documentElement.style.setProperty('--system-status-bar-height', `${statusBarHeight}px`);
    document.documentElement.style.setProperty('--system-nav-bar-height', `${navigationBarHeight}px`);
    document.documentElement.style.setProperty('--dynamic-header-padding', `${headerPadding}px`);
    document.documentElement.style.setProperty('--dynamic-footer-padding', `${footerPadding}px`);

    // Add platform class for CSS targeting
    if (this.isAndroid) {
      document.body.classList.add('android');
    } else if (this.isIOS) {
      document.body.classList.add('ios');
    } else if (this.isWebMobile) {
      document.body.classList.add('web-mobile');
    }
  }

  private calculateStatusBarHeight(): number {
    // Use CSS env() variable if available
    const envValue = this.getCSSEnvValue('safe-area-inset-top');
    if (envValue > 0) {
      return envValue;
    }

    // Conservative fallback calculations
    if (this.isAndroid || this.isWebMobile) {
      // Android status bar - use conservative estimate
      return 24; // Standard Android status bar height
    } else if (this.isIOS) {
      // iOS status bar - conservative estimate
      return 24; // Standard iOS status bar
    }

    return 0;
  }

  private calculateNavigationBarHeight(): number {
    // Use CSS env() variable if available
    const envValue = this.getCSSEnvValue('safe-area-inset-bottom');
    if (envValue > 0) {
      return envValue;
    }

    // Calculate from viewport difference
    const visualViewportHeight = window.visualViewport?.height || window.innerHeight;
    const screenHeight = window.screen.height;
    const statusBarHeight = this.calculateStatusBarHeight();
    
    // Navigation bar height = screen height - viewport height - status bar height
    const calculatedNavHeight = screenHeight - visualViewportHeight - statusBarHeight;
    
    if (calculatedNavHeight > 0 && calculatedNavHeight < 200) { // Sanity check
      return calculatedNavHeight;
    }

    // Conservative fallback values
    if (this.isAndroid || this.isWebMobile) {
      // Android navigation bar - conservative estimate
      return 48; // Standard Android nav bar
    } else if (this.isIOS) {
      // iOS home indicator - conservative
      return 20; // Conservative iOS bottom padding
    }

    return 0;
  }

  private getCSSEnvValue(envVar: string): number {
    // Try to get the actual pixel value from CSS env() variables
    const testElement = document.createElement('div');
    testElement.style.height = `env(${envVar})`;
    testElement.style.position = 'absolute';
    testElement.style.visibility = 'hidden';
    document.body.appendChild(testElement);

    const computedHeight = window.getComputedStyle(testElement).height;
    document.body.removeChild(testElement);

    const pixelValue = parseInt(computedHeight, 10);
    return isNaN(pixelValue) ? 0 : pixelValue;
  }

  private detectAndApplyFontScale() {
    // Detect system font scale for iOS and Android
    // This works by measuring how the browser scales text
    let fontScale = 1.0;

    if (Capacitor.isNativePlatform()) {
      // Create a test element to measure actual vs expected size
      const testElement = document.createElement('div');
      testElement.style.cssText = 'position: absolute; visibility: hidden; font-size: 16px; width: auto; white-space: nowrap;';
      testElement.textContent = 'M'; // Use 'M' as it's typically a square character
      document.body.appendChild(testElement);

      const computedSize = parseFloat(window.getComputedStyle(testElement).fontSize);
      document.body.removeChild(testElement);

      // Calculate scale factor (actual size / expected size)
      fontScale = computedSize / 16;

      // Apply the scale to the root font-size
      // Base font size is 16px, multiply by system scale
      const scaledFontSize = 16 * fontScale;
      document.documentElement.style.fontSize = `${scaledFontSize}px`;

      // Store the scale factor as a CSS variable for potential use
      document.documentElement.style.setProperty('--system-font-scale', fontScale.toString());
    }
  }
}